"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Activity, Server, Wifi, ArrowRight, Globe } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const NODES = [{
  id: 1,
  x: 22,
  y: 52,
  city: "Dallas Hub",
  region: "DFW Metroplex",
  stats: {
    installations: "Last Mile",
    uptime: "100%",
    status: "Active"
  }
}, {
  id: 2,
  x: 78,
  y: 58,
  city: "Atlanta Terminal",
  region: "Southeast Region",
  stats: {
    installations: "Semi-Truck",
    uptime: "99.9%",
    status: "Active"
  }
}, {
  id: 3,
  x: 65,
  y: 45,
  city: "Nashville Hub",
  region: "Regional Link",
  stats: {
    installations: "Heavy Haul",
    uptime: "99.8%",
    status: "Active"
  }
}, {
  id: 4,
  x: 32,
  y: 78,
  city: "Houston Port",
  region: "Texas Coast",
  stats: {
    installations: "Bulk Materials",
    uptime: "99.9%",
    status: "Active"
  }
}, {
  id: 5,
  x: 18,
  y: 55,
  city: "Fort Worth",
  region: "DFW Metroplex",
  stats: {
    installations: "Last Mile",
    uptime: "100%",
    status: "Active"
  }
}, {
  id: 6,
  x: 85,
  y: 82,
  city: "Miami Terminal",
  region: "Southeast Coast",
  stats: {
    installations: "Semi-Truck",
    uptime: "99.7%",
    status: "Active"
  }
}];
interface MapNodeProps extends React.HTMLAttributes<HTMLDivElement> {
  node: (typeof NODES)[0];
}
function MapNode({
  node,
  ...props
}: MapNodeProps) {
  const [open, setOpen] = useState(false);
  return <div data-section-id="38591" {...props} className="absolute -translate-x-1/2 -translate-y-1/2 z-20" style={{
    left: `${node.x}%`,
    top: `${node.y}%`
  }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" className="h-auto w-auto rounded-full p-0 hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0">
            <span className="relative flex h-8 w-8 items-center justify-center">
              {}
              <span className="bg-primary/50 absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 duration-2000"></span>
              {}
              <span className="bg-primary shadow-primary/50 relative inline-flex h-3 w-3 rounded-full shadow-[0_0_15px] transition-transform duration-300 hover:scale-125"></span>
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" className="bg-card/95 border-border/40 text-card-foreground w-64 backdrop-blur-xl rounded-sm">
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <h4 className="font-default text-lg font-bold tracking-tight text-foreground">
                {node.city}
              </h4>
              <span className={`text-xs font-mono font-medium px-2 py-0.5 rounded-sm ${node.stats.status === "Active" ? "bg-primary/20 text-primary" : "bg-secondary/20 text-secondary"}`}>
                {node.stats.status}
              </span>
            </div>
            <div className="grid gap-2 text-sm font-mono">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Server className="h-3.5 w-3.5" /> Service Tier
                </span>
                <span className="font-bold">
                  {node.stats.installations}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Activity className="h-3.5 w-3.5" /> Reliability
                </span>
                <span className="font-bold text-primary">
                  {node.stats.uptime}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5" /> Focus Area
                </span>
                <span className="text-foreground">{node.region}</span>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>;
}
export default function NationalFootprint() {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // D3 zoom functionality removed due to missing dependency
    // Map remains interactive with node popovers on hover
  }, []);
  return <section id="service-area-map" className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      {}
      <div className="pointer-events-none absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 opacity-20 blur-[120px]">
        <div className="bg-primary h-[500px] w-[500px] rounded-full mix-blend-screen"></div>
      </div>
      <div className="pointer-events-none absolute right-0 bottom-0 translate-x-1/3 translate-y-1/3 opacity-10 blur-[100px]">
        <div className="bg-secondary h-[600px] w-[600px] rounded-full mix-blend-screen"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
          {}
          <div className="flex flex-col justify-center lg:col-span-4 xl:col-span-3">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-sm border border-border bg-secondary/10 px-3 py-1 text-xs font-mono font-medium text-primary backdrop-blur-sm">
                <Wifi className="h-3 w-3 animate-pulse" />
                <span>Regional Logistics Network</span>
              </div>

              <h2 className="font-default text-4xl font-bold leading-tight tracking-tighter text-foreground md:text-5xl">
                Strategic Reach <br />
                <span className="text-muted-foreground">at Scale</span>
              </h2>

              <p className="font-mono text-muted-foreground text-lg leading-relaxed">Legacy delivers industrial materials with precision. From DFW last-mile routes to heavy-haul semi-truck operations across the Southeast, we ensure your bulk materials arrive on time. Contact dispatch at +1-469-590-4364.</p>

              <div className="grid grid-cols-2 gap-6 border-t border-border pt-6">
                <div>
                  <div className="font-default text-3xl font-bold text-foreground">Tier 1</div>
                  <div className="font-mono text-muted-foreground text-xs uppercase tracking-wider">
                    DFW Last Mile
                  </div>
                </div>
                <div>
                  <div className="font-default text-3xl font-bold text-foreground">Tier 2</div>
                  <div className="font-mono text-muted-foreground text-xs uppercase tracking-wider">
                    Southeast Haul
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button asChild size="lg" className="w-full sm:w-auto rounded-sm bg-primary text-primary-foreground font-bold hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] transition-all">
                  <Link to="#">
                    Schedule Delivery <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="relative h-[500px] w-full overflow-hidden rounded-sm border border-border bg-card/40 shadow-2xl backdrop-blur-sm lg:col-span-8 xl:col-span-9">
            {}
            <div ref={mapContainerRef} className="h-full w-full">
              {}
              <div ref={mapContentRef} className="relative h-full w-full origin-top-left">
                {}
                <svg viewBox="0 0 1000 600" className="pointer-events-none absolute inset-0 h-full w-full opacity-90" preserveAspectRatio="xMidYMid slice">
                  <defs>
                    <linearGradient id="mapGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="hsl(var(--secondary))" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.1" />
                    </linearGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {}
                  <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                    <path d="M 50 0 L 0 0 0 50" fill="none" stroke="hsl(var(--secondary))" strokeOpacity="0.15" strokeWidth="1" />
                  </pattern>
                  <rect width="100%" height="100%" fill="url(#grid)" />

                  {}
                  <g transform="translate(50, 50) scale(0.9)">
                    <path d="M50,100 L120,80 L250,60 L450,50 L650,60 L800,120 L880,250 L820,400 L750,480 L600,500 L400,480 L200,420 L50,300 Z" fill="url(#mapGradient)" stroke="hsl(var(--secondary))" strokeWidth="2" strokeOpacity="0.6" className="transition-all duration-500 hover:stroke-opacity-100" filter="url(#glow)" />
                    {}
                    <line x1="120" y1="80" x2="200" y2="420" stroke="hsl(var(--primary))" strokeWidth="0.5" strokeOpacity="0.3" />
                    <line x1="450" y1="50" x2="400" y2="480" stroke="hsl(var(--primary))" strokeWidth="0.5" strokeOpacity="0.3" />
                    <line x1="650" y1="60" x2="600" y2="500" stroke="hsl(var(--primary))" strokeWidth="0.5" strokeOpacity="0.3" />
                    <circle cx="220" cy="280" r="120" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" strokeOpacity="0.2" strokeDasharray="10 5" />
                  </g>
                </svg>

                {}
                <div className="absolute inset-0 h-full w-full">
                  {NODES.map((node, i) => <MapNode key={node.id} node={node} data-index={i} />)}
                </div>
              </div>
            </div>

            {}
            <div className="pointer-events-none absolute bottom-6 left-6 z-20">
              <div className="flex items-center gap-2 text-xs font-mono text-foreground/60 bg-background/40 px-2 py-1 rounded-sm border border-border">
                <MapPin className="h-3 w-3" />
                <span>Hover to explore service hubs</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}